import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const [tab, setTab] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = tab === "signin" ? await login(email, password) : await register(name, email, password);
      toast.success(`Welcome${tab === "signup" ? " to PAQVERO" : " back"}, ${user.name.split(" ")[0]}`);
      navigate(location.state?.from || (["admin", "owner"].includes(user.role) ? "/admin" : "/shop"));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 pb-16 pt-32" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <motion.img
            src="/paqvero-logo-lockup.png"
            alt="PAQVERO"
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="mx-auto mb-6 h-28 w-auto drop-shadow-[0_8px_20px_rgba(200,135,50,0.18)]"
            data-testid="login-logo"
          />
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {tab === "signin" ? "Welcome back." : "Create your account."}
          </h1>
          <p className="mt-2 text-sm text-3">
            {tab === "signin" ? "Sign in to track orders and check out faster." : "Join PAQVERO to save carts, addresses and track every order."}
          </p>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-7">
          <div className="mb-6 grid grid-cols-2 rounded-xl border border-line bg-input p-1">
            {[["signin", "Sign In"], ["signup", "Create Account"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`login-tab-${key}`}
                onClick={() => { setTab(key); setError(""); }}
                className={`rounded-full py-2.5 text-sm font-bold transition-all ${
                  tab === key ? "bg-[#C88732] text-[#FFFEFA] shadow-[0_2px_10px_rgba(200,135,50,0.28)]" : "text-2 hoverable-t1"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {tab === "signup" && (
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-3">Full name</label>
                <input
                  data-testid="login-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Aarav Mehta"
                  className="h-12 w-full rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-3">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="h-12 w-full rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-3">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder={tab === "signup" ? "Minimum 8 characters" : "Your password"}
                  className="h-12 w-full rounded-xl border border-line bg-input px-4 pr-12 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-3 hoverable-t1"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="rounded-lg border border-[#B9473F]/30 bg-[#B9473F]/[0.07] px-4 py-2.5 text-sm text-[#B9473F]" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#C88732] text-sm font-bold text-[#FFFEFA] shadow-[0_2px_10px_rgba(200,135,50,0.28)] transition-all hover:bg-[#A96C1E] active:bg-[#8A561A] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {tab === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">
            <span className="h-px flex-1 bg-hover" />
            or
            <span className="h-px flex-1 bg-hover" />
          </div>

          <button
            data-testid="login-google-btn"
            onClick={googleLogin}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-strong bg-surface text-sm font-bold text-1 transition-colors hover:border-[#C88732]/50 hover:bg-[#C88732]/[0.08] hover:text-[#A96C1E]"
          >
            <svg className="h-4.5 w-4.5 h-[18px] w-[18px]" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.72 12 .72 7.44.72 3.56 3.36 1.68 7.32l3.66 2.84C6.24 7.14 8.88 5.04 12 5.04z"/>
              <path fill="#4285F4" d="M23.28 12.24c0-.88-.08-1.72-.22-2.52H12v4.76h6.34c-.28 1.48-1.12 2.74-2.4 3.58l3.68 2.86c2.16-2 3.66-4.94 3.66-8.68z"/>
              <path fill="#FBBC05" d="M5.34 14.36a7.2 7.2 0 0 1 0-4.72L1.68 6.8a11.28 11.28 0 0 0 0 10.4l3.66-2.84z"/>
              <path fill="#34A853" d="M12 23.28c2.96 0 5.46-.98 7.28-2.66l-3.68-2.86c-1 .68-2.3 1.08-3.6 1.08-3.12 0-5.76-2.1-6.66-4.96l-3.66 2.84c1.88 3.96 5.76 6.6 10.32 6.6z"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-5 text-center text-xs text-3">
            Guest checkout is always available — no account needed.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
